import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Link,
  Chip,
  Pagination as MuiPagination,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  InputAdornment,
  Card,
  Tooltip,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import FilterModal from './FilterModal';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarsIcon from '@mui/icons-material/Stars';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import ApiService from '../../../../services/api';
import SharedPlanChip from '../../../PlanChip';
import logger from '../../../../utils/logger';
import { useAdminData } from '../../../../contexts/AdminDataContext';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: '#fff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '12px',
  background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  overflow: 'hidden',
}));

const Th = styled(TableCell)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  color: '#ffffff',
  fontWeight: 600,
  padding: '16px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const Td = styled(TableCell)(({ theme }) => ({
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#eff6ff',
  },
}));

const FooterBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '24px',
  padding: '16px 20px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
  },
}));





/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
const ClientListing = () => {
  const { clients: cachedClients, loading: contextLoading, error: contextError, actions } = useAdminData();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 20;

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [requestsDialogOpen, setRequestsDialogOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [assigningIds, setAssigningIds] = useState(false);

  // Handler to assign ESC IDs to all clients without one
  const handleAssignClientIds = async () => {
    if (!window.confirm('This will assign unique IDs to all leads (L prefix) and clients (ESC prefix) who don\'t have one. Continue?')) return;

    setAssigningIds(true);
    try {
      const response = await ApiService.post('/admin/leads/assign-client-ids');
      const result = response?.data || response;
      alert(`Success! Assigned IDs to ${result.total_updated || 0} records.`);
      // Refresh clients to show updated IDs
      if (actions.fetchClients) actions.fetchClients(true);
    } catch (err) {
      console.error('Failed to assign client IDs:', err);
      alert('Failed to assign IDs: ' + (err.message || 'Unknown error'));
    } finally {
      setAssigningIds(false);
    }
  };

  // Use cached data or fetch if none
  // Only show clients with GMB profiles (location_id or locationId must exist)
  // UPDATED: Show ALL clients, even without GMB profile
  const isInitial = cachedClients === null;
  const clients = (cachedClients || []);
  const loading = isInitial || (contextLoading.clients && clients.length === 0);
  const error = contextError.clients || '';

  useEffect(() => {
    // Background refresh/initial fetch
    actions.fetchClients();
  }, [actions]);

  useEffect(() => {
    // fetch pending requests count separately as it changes often
    const loadRequests = async () => {
      try {
        const r = await ApiService.get('/admin/gmb-account/phone-change-requests');
        const all = (r?.data?.requests || r?.requests || []);
        setPendingRequests(all.filter(q => q.status === 'pending'));
      } catch (e) { /* ignore */ }
    };
    loadRequests();
  }, []);

  const filteredClients = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c => {
      return (
        (c.business_name || '').toString().toLowerCase().includes(q) ||
        (c.email || '').toString().toLowerCase().includes(q) ||
        (c.city || '').toString().toLowerCase().includes(q) ||
        (c.phone_number || '').toString().toLowerCase().includes(q) ||
        (c.plan || '').toString().toLowerCase().includes(q) ||
        (c.lead_uid || '').toString().toLowerCase().includes(q)
      );
    });
  }, [clients, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredClients.length / rowsPerPage));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount]);

  const paginatedClients = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredClients.slice(start, start + rowsPerPage);
  }, [filteredClients, page]);

  const handleOpenGmbProfile = (account) => {
    try {
      const ownerEmail = account.raw?.user?.email || account.email;
      const adminAccessData = {
        isAdminAccess: true,
        originalAdminId: localStorage.getItem('userId'),
        targetUserId: account.raw?.user?.id || account.raw?.user_id || localStorage.getItem('userId'),
        targetUserEmail: ownerEmail || 'unassigned@system.local',
        targetUserName: account.raw?.user?.full_name || account.raw?.user?.name || account.business_name || 'Unassigned',
        gmbAccounts: [account.raw],
        defaultProfile: {
          id: account.locationId,
          locationId: account.locationId,
          accountId: account.raw?.account_id || null,
          name: account.business_name,
          locationData: account.raw?.location_data || {}
        },
        timestamp: Date.now()
      };

      const tempKey = `admin_viewing_gmb_${account.locationId}_${Date.now()}`;
      const serialized = JSON.stringify(adminAccessData);
      sessionStorage.setItem(tempKey, serialized);
      sessionStorage.setItem('adminAccessData', serialized);
      sessionStorage.setItem('adminViewingGmbTempKey', tempKey);
      try { localStorage.setItem(tempKey, serialized); localStorage.setItem('latestAdminAccessTempKey', tempKey); } catch (e) { /* ignore */ }

      const dashboardUrl = `/dashboard?adminAccess=true&tempKey=${tempKey}&locationId=${account.locationId}`;
      window.open(dashboardUrl, '_blank');
    } catch (err) {
      logger.error('Error opening GMB profile:', err);
      alert('Failed to open GMB profile.');
    }
  };



  const activePlanCount = clients.filter(c => c.plan === 'active' || c.plan === 'pro' || c.plan === 'advance' || c.plan === 'basic').length;
  const basicCount = clients.filter(c => c.plan === 'basic').length;
  const advanceCount = clients.filter(c => c.plan === 'advance' || c.plan === 'active').length;
  const proCount = clients.filter(c => c.plan === 'pro').length;
  const noPlanCount = clients.filter(c => !c.plan || c.plan === 'none').length;

  return (
    <Container>
      <Header>
        <TitleSection>
          <TitleIcon>
            <PeopleIcon fontSize="large" />
          </TitleIcon>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1f2937">Client Management</Typography>
            <Typography variant="body2" color="text.secondary">View and manage all registered business clients</Typography>
          </Box>
        </TitleSection>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <SearchField
            placeholder="Search clients..."
            size="small"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#3b82f6' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320 }}
          />
          <IconButton
            color="primary"
            onClick={() => setRequestsDialogOpen(true)}
            sx={{
              bgcolor: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: '#eff6ff' }
            }}
          >
            <Badge badgeContent={pendingRequests.length} color="error">
              <NotificationsIcon sx={{ color: '#3b82f6' }} />
            </Badge>
          </IconButton>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={(event) => { setAnchorEl(event.currentTarget); setFilterModalOpen(true); }}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' }
            }}
          >
            Filter
          </Button>
        </Box>
      </Header>


      <TableContainerStyled component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#3b82f6' }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading client data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : filteredClients.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <BusinessIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography color="text.secondary">No clients found matching your search.</Typography>
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <Th sx={{ width: 60 }}>#</Th>
                <Th>Business Name</Th>
                <Th>Contact Email</Th>
                <Th>Phone Number</Th>
                <Th>Account Type</Th>
                <Th>Current Plan</Th>
                <Th align="center">Actions</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedClients.map((client, idx) => {
                const serial = (page - 1) * rowsPerPage + idx + 1;
                return (
                  <StyledTableRow key={client.id || `${client.locationId}-${idx}`}>
                    <Td>{serial}</Td>
                    <Td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: '#eff6ff',
                            color: '#3b82f6',
                            fontWeight: 700,
                            width: 36,
                            height: 36,
                            fontSize: '14px',
                            border: '1px solid #dbeafe'
                          }}
                        >
                          {(client.business_name || 'U').charAt(0)}
                        </Avatar>
                        <Box>
                          <Link
                            component={RouterLink}
                            to={`/admin-dashboard/client/${client.id}`}
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            <Typography variant="subtitle2" color="#1f2937" sx={{ fontWeight: 700 }}>
                              {client.business_name}
                            </Typography>
                          </Link>
                          <Typography variant="caption" color="text.secondary">{client.city || '—'}</Typography>
                        </Box>
                      </Box>
                    </Td>
                    <Td><Typography variant="body2" color="#4b5563">{client.email || client.lead?.email || client.raw?.user?.email || '—'}</Typography></Td>
                    <Td>
                      <Typography variant="body2" color="#4b5563">{client.phone_number || '—'}</Typography>
                      {client.phoneChangeRequest && client.phoneChangeRequest.status === 'pending' && (
                        <Chip label="Edit Pending" size="small" color="warning" sx={{ height: 20, fontSize: '10px', ml: 1 }} />
                      )}
                    </Td>
                    <Td>
                      <Chip
                        label={client.account_type || 'User'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '11px', fontWeight: 500 }}
                      />
                    </Td>
                    <Td>
                      {client.plan ? (
                        <SharedPlanChip plan={client.plan} />
                      ) : (
                        <Chip label={'NO PLAN'} size="small" sx={{ fontWeight: 700, borderRadius: '8px', fontSize: '10px' }} />
                      )}
                    </Td>
                    <Td align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {(client.locationId || client.location_id) ? (
                          <Tooltip title="Open GMB Dashboard">
                            <ActionButton onClick={() => handleOpenGmbProfile(client)} sx={{ color: '#3b82f6' }}>
                              <OpenInNewIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">No GMB</Typography>
                        )}
                      </Box>
                    </Td>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainerStyled>

      <FooterBox>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MuiPagination
            count={pageCount}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
            size="medium"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Showing {filteredClients.length === 0 ? 0 : (Math.min((page - 1) * rowsPerPage + 1, filteredClients.length))} - {filteredClients.length === 0 ? 0 : Math.min(page * rowsPerPage, filteredClients.length)} of {filteredClients.length} clients
          </Typography>
        </Box>
      </FooterBox>
      <Dialog open={requestsDialogOpen} onClose={() => setRequestsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pending Phone Change Requests</DialogTitle>
        <DialogContent>
          <List>
            {pendingRequests.length === 0 ? (
              <ListItem><ListItemText primary="No pending requests" /></ListItem>
            ) : pendingRequests.map(r => (
              <ListItem key={r.id} divider secondaryAction={<Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" color="primary" variant="contained" onClick={async () => {
                  try {
                    const adminId = localStorage.getItem('userId');
                    await ApiService.post(`/admin/gmb-account/phone-change-request/${r.id}/approve`, { adminUserId: adminId });
                    alert('Approved and applied');
                    setPendingRequests(prev => prev.filter(p => p.id !== r.id));
                    actions.fetchClients(true);
                  } catch (e) { alert('Failed to approve'); }
                }}>Approve</Button>
                <Button size="small" color="error" variant="outlined" onClick={async () => {
                  try {
                    const adminId = localStorage.getItem('userId');
                    await ApiService.post(`/admin/gmb-account/phone-change-request/${r.id}/reject`, { adminUserId: adminId });
                    alert('Rejected');
                    setPendingRequests(prev => prev.filter(p => p.id !== r.id));
                  } catch (e) { alert('Failed to reject'); }
                }}>Reject</Button>
              </Box>}>
                <ListItemText primary={`${r.requested_by_email || r.requested_by_user_id || 'User'} -> ${r.new_number}`} secondary={`Profile: ${r.location_id || r.gmb_account_id} • Status: ${r.status} • Requested: ${new Date(r.created_at).toLocaleString()}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <FilterModal open={filterModalOpen} onClose={() => { setFilterModalOpen(false); setAnchorEl(null); }} anchorEl={anchorEl} />
    </Container>
  );
};

export default ClientListing;